import {
  Component,
  lazy,
  Suspense,
  useEffect,
  useState,
  type ComponentType,
  type ErrorInfo,
  type LazyExoticComponent,
  type ReactNode,
} from 'react';
import { HalloweenBossIntroControls } from './components/HalloweenBossIntroControls';
import { HalloweenBossHud } from './components/HalloweenBossHud';
import { Joystick } from './components/Joystick';
import { Icon } from './components/Icon';
import { RallyButton } from './components/RallyButton';
import { LoadingScreen } from './components/LoadingScreen';
import { SplashBackground } from './components/SplashBackground';
import { HatchAnimationModal } from './components/HatchAnimationModal';
import { BossRewardModal } from './components/BossRewardModal';
import { ActiveBuffsHud } from './components/ActiveBuffsHud';
import { BiomeIntroBanner } from './components/BiomeIntroBanner';
import { AchievementToast } from './components/AchievementToast';
import { DirectTradeModal, TradeNotice } from './components/DirectTradeModal';
import { StallSaleNotifications } from './components/StallSaleNotifications';
import { ActionMenu } from './components/ActionMenu';
import { PlazaCommsBar } from './components/PlazaCommsBar';
import { EduInteractBar } from './components/EduInteractBar';
import { FabTutorialArrow } from './components/FabTutorialArrow';
import { useGameStore } from './store/gameStore';
import { formatNumberCompact } from '@/utils/formatNumber';
import { eventBus } from '@/shared/EventBus';
import { getActiveEvents } from '@/config/events.config';
import { musicController } from '@/game/audio/MusicController';

/**
 * Ekrany otwierane dopiero po akcji gracza nie należą do paczki startowej.
 * Każdy import ma statyczną ścieżkę, więc Vite tworzy osobny, hashowany
 * chunk i pobiera go tylko przy pierwszym renderze danego ekranu.
 */
type PreloadableLazy<T extends ComponentType<any>> = LazyExoticComponent<T> & {
  preload: () => Promise<unknown>;
};

function lazyNamedComponent<K extends string, M extends Record<K, ComponentType<any>>>(
  importer: () => Promise<M>,
  exportName: K,
): PreloadableLazy<M[K]> {
  let loading: Promise<{ default: M[K] }> | undefined;
  const load = (): Promise<{ default: M[K] }> => (
    loading ??= importer().then((module) => {
      // Anulowany `vite:preloadError` rozwiązuje import jako undefined. Reload
      // już trwa — trzymaj fallback Suspense zamiast pokazywać ErrorBoundary.
      if (module == null) return new Promise<never>(() => {});
      return { default: module[exportName] };
    })
  );
  return Object.assign(lazy(load), { preload: load });
}

const AvatarSelectorModal = lazyNamedComponent(
  () => import('./components/AvatarSelectorModal'),
  'AvatarSelectorModal',
);
const StarterPetSelectorModal = lazyNamedComponent(
  () => import('./components/StarterPetSelectorModal'),
  'StarterPetSelectorModal',
);
const EggShopModal = lazyNamedComponent(() => import('./components/EggShopModal'), 'EggShopModal');
const EggInventoryModal = lazyNamedComponent(
  () => import('./components/EggInventoryModal'),
  'EggInventoryModal',
);
const PetInventoryModal = lazyNamedComponent(
  () => import('./components/PetInventoryModal'),
  'PetInventoryModal',
);
const MergePanel = lazyNamedComponent(() => import('./components/MergePanel'), 'MergePanel');
const UpgradesModal = lazyNamedComponent(() => import('./components/UpgradesModal'), 'UpgradesModal');
const BiomeMapModal = lazyNamedComponent(() => import('./components/BiomeMapModal'), 'BiomeMapModal');
const LeaderboardModal = lazyNamedComponent(
  () => import('./components/LeaderboardModal'),
  'LeaderboardModal',
);
const AccountModal = lazyNamedComponent(() => import('./components/AccountModal'), 'AccountModal');
const DailyRewardModal = lazyNamedComponent(
  () => import('./components/DailyRewardModal'),
  'DailyRewardModal',
);
const MyStallModal = lazyNamedComponent(() => import('./components/MyStallModal'), 'MyStallModal');
const TradeHistoryModal = lazyNamedComponent(() => import('./components/TradeHistoryModal'), 'TradeHistoryModal');
const BrowseStallModal = lazyNamedComponent(
  () => import('./components/BrowseStallModal'),
  'BrowseStallModal',
);
const TraderMenuModal = lazyNamedComponent(
  () => import('./components/TraderMenuModal'),
  'TraderMenuModal',
);
const EggExchangeModal = lazyNamedComponent(
  () => import('./components/EggExchangeModal'),
  'EggExchangeModal',
);
const BankHubModal = lazyNamedComponent(() => import('./components/BankHubModal'), 'BankHubModal');
const BankAlbumModal = lazyNamedComponent(
  () => import('./components/BankAlbumModal'),
  'BankAlbumModal',
);
const PosterGalleryModal = lazyNamedComponent(
  () => import('./components/PosterGalleryModal'),
  'PosterGalleryModal',
);
const BankWorkModal = lazyNamedComponent(() => import('./components/BankWorkModal'), 'BankWorkModal');
const BankDepositModal = lazyNamedComponent(
  () => import('./components/BankDepositModal'),
  'BankDepositModal',
);
const BankVaultModal = lazyNamedComponent(() => import('./components/BankVaultModal'), 'BankVaultModal');
const NpcSellModal = lazyNamedComponent(() => import('./components/NpcSellModal'), 'NpcSellModal');
const PotionShopModal = lazyNamedComponent(
  () => import('./components/PotionShopModal'),
  'PotionShopModal',
);
const PotionBagModal = lazyNamedComponent(() => import('./components/PotionBagModal'), 'PotionBagModal');
const AchievementsModal = lazyNamedComponent(
  () => import('./components/AchievementsModal'),
  'AchievementsModal',
);
const EventAlbumModal = lazyNamedComponent(
  () => import('./components/EventAlbumModal'),
  'EventAlbumModal',
);
const EventDailyRewardModal = lazyNamedComponent(
  () => import('./components/EventDailyRewardModal'),
  'EventDailyRewardModal',
);
const AvatarCustomizeModal = lazyNamedComponent(
  () => import('./components/AvatarCustomizeModal'),
  'AvatarCustomizeModal',
);
const SettingsModal = lazyNamedComponent(() => import('./components/SettingsModal'), 'SettingsModal');

/**
 * App.tsx — root komponentu React UI.
 *
 * HUD pokazuje monety + kryształy + moc (nazwa biomu NIE jest stała — pojawia się
 * jako napis przy wejściu, `BiomeIntroBanner`). Stan czytany z Zustand'a przez
 * osobne selectory (każdy re-renderuje TYLKO gdy jego pole się zmieni).
 *
 * Wszystkie ikony są <Icon> z auto-fallbackiem na emoji — jeśli PNG istnieje
 * w `assets/ui/`, używa go; jeśli nie, pokazuje emoji.
 */
export function App() {
  // Atomowe selectory dla każdej waluty/wartości HUD — gdyby był jeden
  // selector zwracający `{ coins, gems, power }`, Zustand re-renderowałby App
  // za każdym razem gdy ZMIENI się którekolwiek z pól (przez shallow compare
  // nowego obiektu vs starego). Atomowe selektory są bezpieczniejsze pod
  // kątem performance'u i intencji.
  const coins = useGameStore((s) => s.coins);
  // Etap 5, 5.0.A — kryształy jako 3. waluta HUD. Drop z dest-crystal (już
  // w destructibles.config.ts), daily reward, merge bonus, NPC sell.
  const gems = useGameStore((s) => s.gems);
  // Eventy (EV-1a) — portfel walut eventowych. Plakietka pokazywana TYLKO dla
  // walut eventów AKTYWNYCH teraz (getActiveEvents). Event wyłączony =>
  // getActiveEvents() => [] => żadnej plakietki (zero wpływu na żywą grę).
  const eventCurrencies = useGameStore((s) => s.eventCurrencies);
  const activeEventCurrencies = getActiveEvents()
    .map((e) => e.currency)
    .filter((c): c is NonNullable<typeof c> => c != null);
  const power = useGameStore((s) => s.power);
  const goldRainbowCoinBonus = useGameStore((s) => s.goldRainbowCoinBonus);
  const openModal = useGameStore((s) => s.openModal);
  const setOpenModal = useGameStore((s) => s.setOpenModal);
  const bossIntroActive = useGameStore((s) => s.bossIntroActive);
  const activeScene = useGameStore((s) => s.activeScene);
  const currentBiomeId = useGameStore((s) => s.currentBiomeId);
  const hatchPhase = useGameStore((s) => s.hatchPhase);
  // Trwa portal teleportu (TeleportScene) — chowamy UI tak jak przy hatchu,
  // żeby canvas z animacją był czysty.
  const isTeleporting = useGameStore((s) => s.isTeleporting);
  // Sesja 30: 3-fazowy onboarding state. Sesja 33: + gate isAuthenticated.
  // Sesja 34B: + gate saveLoaded (ładowanie stanu z serwera).
  // Sesja guest claim cleanup: usunięto gate `!isAuthenticated` + AuthModal —
  // tryb gościa gwarantuje, że `resolveSession()` w `main.tsx` ustawia
  // `setAuth(true, user)` PRZED renderem App (cookie / re-login / nowe konto-
  // gość). Jeśli wszystkie 3 ścieżki padną → rzut → `showBootstrapError`
  // (DOM-only fallback, App się nigdy nie zamontuje). Czyli `isAuthenticated`
  // w punkcie renderu App jest zawsze `true`; gate był martwym kodem.
  const saveLoaded = useGameStore((s) => s.saveLoaded);
  const avatarSelected = useGameStore((s) => s.avatarSelected);
  const starterPetsChosen = useGameStore((s) => s.starterPetsChosen);
  // Faza ładowania grafik (Phaser PreloadScene) — `createGame()` startuje PO
  // onboardingu, więc pasek postępu pokazujemy NA splashu z logiem zamiast
  // surowego zielonego tła Phasera z osobnym paskiem.
  const assetsLoaded = useGameStore((s) => s.assetsLoaded);
  const assetLoadProgress = useGameStore((s) => s.assetLoadProgress);


  // Sesja guest claim cleanup: 4-fazowy onboarding gate (z 5 → 4 po usunięciu
  // `!isAuthenticated` → AuthModal).
  //   0. !saveLoaded → LoadingScreen (ładowanie stanu z serwera)
  //   1. !avatarSelected → AvatarSelectorModal (wybierz skin)
  //   2. !starterPetsChosen → StarterPetSelectorModal (wybierz 2 pety)
  //   3. wszystko true → normal HUD + FabTutorialArrow (jeśli !hasSeenFabTutorial)
  //
  // KOLEJNOSC W main.tsx (sesja guest core - auth zawsze przed renderem):
  //   resolveSession() (cookie / re-login / nowe konto-gość)
  //     → setAuth(true, user) → render <App />
  //     → saveManager.load() (ServerSaveAdapter, GET /saves/me + ew. import)
  //     → hydrate store → setSaveLoaded(true)
  //     → waitForOnboardingCompletion → createGame()
  //
  // 2026-06-01: minimum display time dla LoadingScreen (1.5 s) — gracz
  // ZAWSZE widzi splash z tytułową grafiką, niezależnie od tego jak szybko
  // GET /saves/me zwraca. Bez tego loading mógł błysnąć <500ms i splash
  // znikał zanim oko zarejestrowało.
  const MIN_LOADING_DISPLAY_MS = 1500;
  const [mountTime] = useState(() => Date.now());
  const [minTimePassed, setMinTimePassed] = useState(false);
  useEffect(() => {
    const elapsed = Date.now() - mountTime;
    const remaining = Math.max(0, MIN_LOADING_DISPLAY_MS - elapsed);
    if (remaining === 0) {
      setMinTimePassed(true);
      return;
    }
    const t = setTimeout(() => setMinTimePassed(true), remaining);
    return () => clearTimeout(t);
  }, [mountTime]);

  // Te ekrany są przewidywalne zanim staną się widoczne. Preload nakłada ich
  // mały request na splash / pasek grafik, zamiast dokładać kolejny RTT po nim.
  useEffect(() => {
    if (!saveLoaded) return;
    if (!avatarSelected) void AvatarSelectorModal.preload().catch(() => undefined);
    if (!starterPetsChosen) void StarterPetSelectorModal.preload().catch(() => undefined);
  }, [saveLoaded, avatarSelected, starterPetsChosen]);

  useEffect(() => {
    if (openModal === 'daily') void DailyRewardModal.preload().catch(() => undefined);
  }, [openModal]);

  // saveLoaded gate eliminuje mignięcie AvatarSelector z placeholder store
  // state (przed odpowiedzią GET /saves/me store ma avatarSelected=false).
  //
  // 2026-06-01: wszystkie 3 onboarding fazy renderują się NA splash background
  // (`SplashBackground` = title_splash.png + subtle meadow tint). LoadingScreen
  // czeka też na `minTimePassed` — gracz ZAWSZE zobaczy splash przez 1.5 s.
  const shouldShowLoading = !saveLoaded || !minTimePassed;
  // Po wyborze avatara + starterów Phaser ładuje grafiki (PreloadScene).
  // Trzymamy splash z paskiem postępu na wierzchu aż `assetsLoaded`, żeby
  // gracz nie zobaczył gołego zielonego tła Phasera między onboardingiem a grą.
  const onboardingChosen = avatarSelected && starterPetsChosen;
  // `!shouldShowLoading` w predykacie czyni go też semantycznie rozłącznym z
  // fazą ładowania save/minTime (Codex LOW 1) — nie tylko dzięki kolejności
  // branchy. Bez tego boolean mógłby być `true` równolegle ze splashem-save
  // (gracz z ukończonym onboardingiem przed upływem min 1.5 s).
  const shouldShowAssetLoading = !shouldShowLoading && onboardingChosen && !assetsLoaded;
  const isOnboarding =
    shouldShowLoading || !avatarSelected || !starterPetsChosen || shouldShowAssetLoading;
  if (isOnboarding) {
    return (
      <div className="relative w-full h-full font-game pointer-events-auto">
        <SplashBackground />
        {/* Content layer z `relative z-10` (Codex splash LOW 2 fix) —
            SplashBackground tworzy własny stacking context z `isolate`,
            ale to jego dzieci są w z-0. LoadingScreen i modale potrzebują
            jawnego z-10 żeby siedzieć NAD splashem w jego rodzicu. */}
        <div className="relative z-10 w-full h-full">
          {shouldShowLoading
            ? <LoadingScreen />
            : !avatarSelected
              ? (
                  <Suspense fallback={<LoadingScreen label="Otwieram wybór postaci…" subtitle="Chwilka…" />}>
                    <AvatarSelectorModal />
                  </Suspense>
                )
              : !starterPetsChosen
                ? (
                    <Suspense fallback={<LoadingScreen label="Otwieram wybór petów…" subtitle="Chwilka…" />}>
                      <StarterPetSelectorModal />
                    </Suspense>
                  )
                : <LoadingScreen
                    progress={assetLoadProgress}
                    label="Ładuję grafiki gry…"
                    subtitle="Już prawie gotowe"
                  />}
        </div>
      </div>
    );
  }

  // Sesja 29 — Step A (Wariant A z PHASER_ANIMATION_ANALYSIS):
  // Podczas hatch cinematic Phaser rysuje na pełnym ekranie — chowamy React UI
  // (HUD, joystick, modale pod spodem) żeby canvas nie był przykryty DOM-em.
  // HatchAnimationModal POZOSTAJE zamontowany — jest kontrolerem eventBus i
  // renderuje finalny panel po `hatch:complete`.
  // Ukrywamy całe główne UI gdy gra Phaser pełnoekranowy cinematic: hatch LUB
  // teleport. Oba rysują na pełnym canvasie i chcemy czysty obraz bez DOM.
  const isHatchAnimating = hatchPhase === 'animating';
  const hideGameUi = isHatchAnimating || isTeleporting;

  return (
    // pointer-events-none na rootcie → puste obszary HUD są „przezroczyste" dla
    // kliknięć i przechodzą do canvas Phasera (klik budek na placu, 5.B.5a).
    // Interaktywne dzieci (Joystick, ActionMenu, modale, przyciski) włączają
    // pointer-events-auto JAWNIE. Phaser nie nasłuchuje eventów Window
    // (`Game.ts: input.windowEvents=false`), więc gest z kontrolki DOM nie może
    // zostać ponownie zinterpretowany przez hitbox świata pod spodem.
    <div className="w-full h-full font-game pointer-events-none">
      <AudioBridge />

      {/* Joystick i HUD chowane podczas hatch cinematic ORAZ teleportu (Wariant A). */}
      {!hideGameUi && (
        <div className={bossIntroActive ? 'hidden' : 'contents'}>
          <Joystick />

          {/* TOP-LEFT: zwarty panel statystyk — monety / kryształy / moc w JEDNYM
              boksie (przebudowa: były 3 osobne chipy + plakietka biomu rozciągnięte
              na całą szerokość). Pod panelem buffy z mikstur (ActiveBuffsHud) — buff
              złota ląduje wizualnie pod licznikiem złota.

              Nazwa biomu NIE jest tu już stale wyświetlana — pojawia się jako napis,
              który mignie przy wejściu (BiomeIntroBanner). Pełna lista biomów z
              nazwami jest w „Mapie".

              - `formatNumberCompact()` → "1.5K"/"12M"/"4.2B" (mobile overflow).
              - `tabular-nums` żeby HUD nie skakał przy zmianie cyfr.
              - z-40 jak FAB/„Wróć z placu" — buff klikalny też przy rozwiniętym menu
                (overlay menu jest z-30). Panel walut pointer-events-none (klik
                przechodzi do canvasu); klikalny tylko przycisk buffów. */}
          {activeScene !== 'halloweenBoss' && <div className="absolute top-3 left-3 z-40 flex flex-col items-start gap-1.5 pointer-events-none">
            <div className="bg-white/85 rounded-2xl px-2.5 sm:px-3 py-1 sm:py-1.5 shadow-md flex items-center gap-2.5 sm:gap-3.5">
              <span className="flex items-center gap-1">
                <Icon src="/ui/ui_coin.webp" fallback="🪙" alt="monety" className="w-5 h-5 sm:w-6 sm:h-6" />
                <span className="text-sm sm:text-base font-bold text-yellow-700 tabular-nums">{formatNumberCompact(coins)}</span>
              </span>
              <span className="flex items-center gap-1">
                <Icon src="/ui/ui_gem.webp" fallback="💎" alt="kryształy" className="w-5 h-5 sm:w-6 sm:h-6" />
                <span className="text-sm sm:text-base font-bold text-purple-700 tabular-nums">{formatNumberCompact(gems)}</span>
              </span>
              {/* Waluty eventowe (EV-1a) — plakietka per aktywny event z walutą.
                  Nazwa/ikona z `events.config` (EventCurrencyDef), saldo z
                  store.eventCurrencies. Brak aktywnego eventu => nic się nie renderuje. */}
              {activeEventCurrencies.map((cur) => (
                <span key={cur.id} className="flex items-center gap-1">
                  <Icon
                    src={cur.iconKey}
                    fallback={cur.fallbackEmoji ?? '★'}
                    alt={cur.name.pl}
                    className="w-5 h-5 sm:w-6 sm:h-6"
                  />
                  <span className="text-sm sm:text-base font-bold text-teal-700 tabular-nums">
                    {formatNumberCompact(eventCurrencies[cur.id] ?? 0)}
                  </span>
                </span>
              ))}
              <span className="flex items-center gap-1">
                <Icon src="/ui/ui_power.webp" fallback="⚡" alt="moc" className="w-5 h-5 sm:w-6 sm:h-6" />
                <span className="text-sm sm:text-base font-bold text-blue-700 tabular-nums">{formatNumberCompact(power)}</span>
              </span>
              {/* Bonus złota z petów gold/rainbow — tylko gdy > 0 (inaczej pusty
                  segment). Ukryty na placu (brak destruktibli → chip „+X%" mylił).
                  Oddzielony cienką kreską od walut. */}
              {goldRainbowCoinBonus > 0 && activeScene !== 'plaza' && (
                <span
                  className="flex items-center gap-1 pl-2 sm:pl-2.5 border-l border-meadow-700/15"
                  title="Bonus złota z petów gold/rainbow"
                >
                  <Icon src="/ui/ui_sparkle.webp" fallback="✨" alt="bonus złota" className="w-5 h-5 sm:w-6 sm:h-6" />
                  <span className="text-sm sm:text-base font-bold text-amber-700 tabular-nums">+{goldRainbowCoinBonus}%</span>
                </span>
              )}
            </div>

            {/* Aktywne buffy z mikstur — w tym samym lewym słupku, pod walutami.
                Sam się chowa gdy brak aktywnych buffów. Klik → plecak mikstur. */}
            <ActiveBuffsHud />
          </div>}
          {activeScene === 'halloweenBoss' && <HalloweenBossHud />}

          {/* 5.B.3: przycisk powrotu z placu handlowego do świata. Renderowany
              tylko gdy gracz jest w scenie 'plaza'. Emituje 'plaza:exit' →
              PlazaScene słucha i robi scene.start('WorldScene'). Pozycja: PRAWY
              górny róg (uwolniony po usunięciu plakietki biomu) — nie koliduje ze
              statystykami/buffami w lewym słupku. */}
          {activeScene === 'plaza' && (
            <button
              type="button"
              onClick={() => eventBus.emit('plaza:exit', {})}
              className="absolute top-3 right-3 z-40 pointer-events-auto bg-white/95 hover:bg-white active:scale-95 rounded-2xl shadow-lg px-3 py-2 flex items-center gap-1.5 transition-all"
            >
              <Icon src="/ui/ui_btn_teleport.webp" fallback="🌀" alt="powrót" className="w-6 h-6" />
              <span className="text-xs sm:text-sm font-bold text-meadow-700">Wróć z placu</span>
            </button>
          )}

          {/* ATL-2b: wejście do Sanktuarium Strażnika — tylko w biomie Atlantyda
              (świat). Emituje 'sanctum:enter' → WorldScene teleportuje do areny bossa. */}
          {activeScene === 'world' && currentBiomeId === 'biome-atlantis' && (
            <button
              type="button"
              onClick={() => eventBus.emit('sanctum:enter', {})}
              className="absolute top-3 right-3 z-40 pointer-events-auto bg-white/95 hover:bg-white active:scale-95 rounded-2xl shadow-lg px-3 py-2 flex items-center gap-1.5 transition-all"
            >
              <Icon src="/ui/ui_btn_teleport.webp" fallback="🌀" alt="sanktuarium" className="w-6 h-6" />
              <span className="text-xs sm:text-sm font-bold text-meadow-700">Sanktuarium Strażnika</span>
            </button>
          )}

          {/* ATL-2b: wyjście z Sanktuarium → powrót do Atlantydy. */}
          {activeScene === 'sanctum' && (
            <button
              type="button"
              onClick={() => eventBus.emit('sanctum:exit', {})}
              className="absolute top-3 right-3 z-40 pointer-events-auto bg-white/95 hover:bg-white active:scale-95 rounded-2xl shadow-lg px-3 py-2 flex items-center gap-1.5 transition-all"
            >
              <Icon src="/ui/ui_btn_teleport.webp" fallback="🌀" alt="powrót" className="w-6 h-6" />
              <span className="text-xs sm:text-sm font-bold text-meadow-700">Opuść Sanktuarium</span>
            </button>
          )}

          {/* ATL-2c: „DO BOJU!" — zagrzanie drużyny (wizualny zryw petów, scena 'sanctum'). */}
          {activeScene === 'sanctum' && <RallyButton />}

          {/* BOTTOM-RIGHT: FAB collapsible menu (sesja 30, przebudowa 2-stronicowa) —
              zastąpił zawsze widoczne ActionButton-y dla lepszej mobile ergonomy. Klik
              FAB rozwija stos: strona 1 (6 głównych) + pigułka „więcej" → strona 2 (rzadsze opcje). */}
          <PlazaCommsBar />
          <EduInteractBar />
          {activeScene !== 'halloweenBoss' && <ActionMenu />}

          {/* Tutorial arrow — pokazywana tylko gdy !hasSeenFabTutorial. Wskazuje
              FAB diagonalnie. Sama hide'uje się po: klik FAB, timeout 12s,
              otwarcie modala. Save flag persistowany. */}
          {activeScene !== 'halloweenBoss' && <FabTutorialArrow />}

          {/* Modale — renderowane warunkowo na podstawie openModal w Zustand.
              Też ukrywane podczas hatch animating (pod common przykryciem przez
              wrapper !isHatchAnimating). */}
          <ModalChunkErrorBoundary
            key={openModal ?? 'closed'}
            onClose={() => setOpenModal(null)}
          >
            <Suspense fallback={<ModalChunkFallback onClose={() => setOpenModal(null)} />}>
              {openModal === 'eggs' && <EggShopModal />}
              {openModal === 'eggInventory' && <EggInventoryModal />}
              {openModal === 'pets' && <PetInventoryModal />}
              {openModal === 'merge' && <MergePanel />}
              {openModal === 'upgrades' && <UpgradesModal />}
              {openModal === 'map' && <BiomeMapModal />}
              {openModal === 'leaderboard' && <LeaderboardModal />}
              {openModal === 'account' && <AccountModal />}
              {openModal === 'daily' && <DailyRewardModal />}
              {openModal === 'myStall' && <MyStallModal />}
              {openModal === 'tradeHistory' && <TradeHistoryModal />}
              {openModal === 'browseStall' && <BrowseStallModal />}
              {openModal === 'traderMenu' && <TraderMenuModal />}
              {openModal === 'eggExchange' && <EggExchangeModal />}
              {openModal === 'bankHub' && <BankHubModal />}
              {openModal === 'bankAlbum' && <BankAlbumModal />}
              {openModal === 'posterGallery' && <PosterGalleryModal />}
              {openModal === 'bankWork' && <BankWorkModal />}
              {openModal === 'bankDeposit' && <BankDepositModal />}
              {openModal === 'bankVault' && <BankVaultModal />}
              {openModal === 'npcSell' && <NpcSellModal />}
              {openModal === 'potionShop' && <PotionShopModal />}
              {openModal === 'potionBag' && <PotionBagModal />}
              {openModal === 'achievements' && <AchievementsModal />}
              {openModal === 'eventAlbum' && <EventAlbumModal />}
              {openModal === 'eventDailyReward' && <EventDailyRewardModal />}
              {openModal === 'avatarCustomize' && <AvatarCustomizeModal />}
              {openModal === 'settings' && <SettingsModal />}
            </Suspense>
          </ModalChunkErrorBoundary>
          {/* Handel bezpośredni (5.C.1) — sterowany store.trade (nie openModal),
              bo stan przychodzi z realtime przez PlazaScene. Self-gate na null. */}
          <DirectTradeModal />
          <TradeNotice />
          <StallSaleNotifications />
          {/* Toasty odblokowanych achievementów (5.D.2d) — kolejka ze store. */}
          <AchievementToast />
        </div>
      )}

      {/* Hatch controller + finalny panel — ZAWSZE zamontowany żeby useEffect'y
          (emit hatch:start, listener hatch:complete) żyły. Sam komponent
          warunkowo nic nie renderuje gdy phase='animating' (Phaser pokazuje
          cinematic). Po `hatch:complete` renderuje finalny panel. */}
      <HalloweenBossIntroControls />
      <HatchAnimationModal />

      {/* ATL-2: modal nagrody za pokonanie Strażnika — sterowany eventem
          'sanctum:reward' (scena → React). Zawsze zamontowany, self-gate (null
          gdy brak nagrody). Zamknięcie emituje 'sanctum:exit'. */}
      <BossRewardModal />

      {/* Napis nazwy biomu/lokalizacji który mignie przy wejściu (teleport/plac).
          POZA blokiem `!hideGameUi` celowo — ten blok znika na czas teleportu,
          a komponent musi przeżyć portal, by wykryć przejście isTeleporting
          true→false (moment odsłonięcia świata). Sam się gate'uje (null gdy brak
          napisu) i jest pointer-events-none. */}
      <BiomeIntroBanner />
    </div>
  );
}

// Sesja 30: `ActionButton` przeniesiony do `ActionMenu.tsx` jako wewnętrzny
// element FAB stack'a. App.tsx nie potrzebuje go bezpośrednio.

function AudioBridge(): null {
  const musicEnabled = useGameStore((s) => s.musicEnabled);
  const musicTrackId = useGameStore((s) => s.musicTrackId);

  useEffect(() => {
    musicController.setMusicState(musicEnabled, musicTrackId);
  }, [musicEnabled, musicTrackId]);

  useEffect(() => () => musicController.stop(), []);

  return null;
}

interface ModalChunkFallbackProps {
  onClose: () => void;
}

/** Blokuje input od razu; kartę pokazuje po 150 ms, a wyjście po 5 s. */
function ModalChunkFallback({ onClose }: ModalChunkFallbackProps): JSX.Element {
  const [showCard, setShowCard] = useState(false);
  const [canClose, setCanClose] = useState(false);

  useEffect(() => {
    const showTimer = window.setTimeout(() => setShowCard(true), 150);
    const closeTimer = window.setTimeout(() => setCanClose(true), 5_000);
    return () => {
      window.clearTimeout(showTimer);
      window.clearTimeout(closeTimer);
    };
  }, []);

  return (
    <div
      className={`fixed inset-0 z-50 pointer-events-auto flex items-center justify-center p-4 transition-colors ${
        showCard ? 'bg-black/30' : 'bg-transparent'
      }`}
      role="status"
      aria-live="polite"
      aria-label="Otwieram ekran"
    >
      {showCard && (
        <div className="rounded-2xl bg-white/95 shadow-xl px-5 py-4 flex flex-col items-center gap-3 text-meadow-700 font-bold">
          <div className="flex items-center gap-3">
            <span className="w-6 h-6 rounded-full border-4 border-meadow-200 border-t-meadow-600 motion-safe:animate-spin" />
            <span>Otwieram…</span>
          </div>
          {canClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl bg-gray-100 hover:bg-gray-200 active:scale-95 px-4 py-2 text-sm text-gray-700 transition"
            >
              Zamknij
            </button>
          )}
        </div>
      )}
    </div>
  );
}

interface ModalChunkErrorBoundaryProps {
  children: ReactNode;
  onClose: () => void;
}

interface ModalChunkErrorBoundaryState {
  hasError: boolean;
}

/** Awaria pojedynczego lazy ekranu nie może odmontować HUD-u ani całej gry. */
class ModalChunkErrorBoundary extends Component<
  ModalChunkErrorBoundaryProps,
  ModalChunkErrorBoundaryState
> {
  state: ModalChunkErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ModalChunkErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    console.error('[ModalChunkErrorBoundary] Nie udało się otworzyć ekranu:', error, info.componentStack);
  }

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    return (
      <div className="fixed inset-0 z-50 pointer-events-auto bg-black/30 flex items-center justify-center p-4">
        <div className="rounded-2xl bg-white shadow-xl p-5 max-w-sm w-full text-center">
          <h2 className="text-xl font-bold text-meadow-700 mb-2">Nie udało się otworzyć ekranu</h2>
          <p className="text-sm text-gray-600 mb-4">
            Sprawdź połączenie. Możesz wrócić do gry albo odświeżyć stronę.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={this.props.onClose}
              className="rounded-xl bg-gray-100 hover:bg-gray-200 active:scale-95 px-3 py-2 font-bold text-gray-700 transition"
            >
              Zamknij
            </button>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-xl bg-meadow-500 hover:bg-meadow-600 active:scale-95 px-3 py-2 font-bold text-white transition"
            >
              Odśwież grę
            </button>
          </div>
        </div>
      </div>
    );
  }
}
